// Rendición de ODD (Rodrigo, 2026-10-29). El camión sale siempre con el manifiesto en papel: la ODD se
// rinde por la app del peoneta o, si no, la rinde tesorería aquí. Con el manifiesto en la mano se marca
// solo lo que volvió (lo demás se entregó completo, como en Random), se anota lo cobrado y listo: el
// sistema rinde, recibe los valores, abona a cada cliente y cierra la ODD. La hoja muestra lo que ya se
// pagó por Pago de Clientes para no cobrarlo dos veces. Las que ya están pagadas se cierran de una vez.
// Cada cobro abonado deja su recibo: el comprobante se imprime aquí mismo, como en Pago de Clientes.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api, nuevaClaveIdempotencia } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { ImprimirRecibo } from './Impresos';

type Medio = 'EFECTIVO' | 'CHEQUE' | 'TRANSFERENCIA';
interface OdPorRendir {
  odId: string;
  numero: number;
  nombre: string | null;
  estado: 'EN_RUTA' | 'RENDIDA';
  version: number;
  fechaNegocio: string;
  patente: string | null;
  chofer: string | null;
  recaudador: string | null;
  paradas: number;
  cobranza: number;
  entregasMarcadas: boolean;
  cobrosPendientes: number;
  valoresRecibidos: boolean;
  retornoPendiente: boolean;
  documentado: string;
  pagado: string;
  porCobrar: string;
  credito: string;
  listaParaCerrar: boolean;
  /** Paradas que el cliente no quiso y esperan su NC (la ODD no se cierra mientras falte). */
  ncPendientes: number;
}
interface Linea {
  notaLineaId: string;
  sku: string;
  descripcion: string;
  cargado: string | null;
  entregado: string | null;
  rechazado: string | null;
  motivoRechazo: string | null;
}
interface Documento {
  documentoId: string;
  tipoDte: number;
  folio: string | null;
  total: string;
  notasCredito: string;
  pagado: string;
  saldo: string;
}
interface CobroDeclarado {
  cobroId: string;
  medio: Medio;
  monto: string;
  origen: 'APP' | 'MANUAL';
  anulado: boolean;
  estadoTesoreria: string | null;
  reciboId: string | null;
}
interface Parada {
  notaId: string;
  parada: number;
  numeroVisible: string;
  cliente: string;
  clienteEntidadId: string;
  condicionPago: string;
  cobroContraEntrega: boolean;
  estado: string;
  marcada: boolean;
  lineas: Linea[];
  aCobrar: { modo: 'CONTADO' | 'COBRANZA' | 'CREDITO'; total: string };
  documentos: Documento[];
  cobros: CobroDeclarado[];
  /** El cliente no quiso lo devuelto y falta su NC: el documento al que hay que hacérsela. */
  ncPendiente: { documentoId: string | null; folio: number | null } | null;
}
interface Hoja {
  odId: string;
  numero: number;
  nombre: string | null;
  estado: string;
  version: number;
  fechaNegocio: string;
  patente: string | null;
  chofer: string | null;
  recaudador: string | null;
  retorno: { pendiente: boolean };
  paradas: Parada[];
}
interface Resultado {
  numero: number;
  entregas: number;
  cobros: number;
  rendida: boolean;
  imputados: Array<{ cobroId: string; reciboId: string }>;
  errores: Array<{ cobroId: string; codigo: string; mensaje: string }>;
  porVerificar: number;
  cerrada: boolean;
  retornoPendiente: boolean;
  ncPendientes: number;
}
type Entrega = 'COMPLETA' | 'DEVOLUCION' | 'RECHAZADA';
/** Lo devuelto de una nota facturada: NC (no lo quiere) o REENTREGA (lo quiere otro día). */
type Destino = 'NC' | 'REENTREGA';
interface CobroNuevo {
  cobroId: string;
  notaId: string;
  medio: Medio;
  monto: string;
  banco: string;
  numero: string;
  fecha: string;
  referencia: string;
  verificada: boolean;
}

const NOMBRE_MEDIO: Record<Medio, string> = {
  EFECTIVO: 'Efectivo',
  CHEQUE: 'Cheque',
  TRANSFERENCIA: 'Transferencia',
};
const TIPO_DOC: Record<number, string> = { 33: 'FAC', 34: 'FEX', 39: 'BOL', 41: 'BEX', 56: 'ND' };
const soloDigitos = (t: string) => t.replace(/\D/g, '');
/** Lo cargado sin ceros de sobra ("48.0000" → "48"): lo entregado si nada volvió. */
const cargadoBase = (l: { cargado: string | null }) => String(Number(l.cargado ?? '0'));
const suma = (xs: string[]) => xs.reduce((s, x) => s + BigInt(x || '0'), 0n);
const cantidadValida = (t: string) => /^\d+(\.\d{1,4})?$/.test(t);
const condicion = (c: string) =>
  ({ contado: 'Contado', transferencia: 'Transferencia', cheque_al_dia: 'Cheque al día' })[c] ??
  (c ? `Crédito (${c.replace(/_/g, ' ')})` : 'Crédito');
const medioPorCondicion = (c: string): Medio =>
  c === 'transferencia' ? 'TRANSFERENCIA' : c === 'cheque_al_dia' ? 'CHEQUE' : 'EFECTIVO';

export function RendicionOdd({ alHacerNc }: { alHacerNc?: (documentoId: string) => void }) {
  const [abierta, setAbierta] = useState<string | null>(null);
  return (
    <div className="pantalla consulta recepcion-rutas rendicion-odd" data-pantalla="rendicion-odd">
      <h2>Rendición de ODD</h2>
      <p className="apagado">
        Las ODD que no se rindieron por la app. Con el manifiesto en la mano: marca solo lo que
        volvió, anota lo cobrado y listo. Lo cobrado se abona a cada cliente y la ODD se cierra.
      </p>
      {abierta ? (
        <Rendir odId={abierta} alVolver={() => setAbierta(null)} alHacerNc={alHacerNc} />
      ) : (
        <Lista alAbrir={setAbierta} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- lista

type Filtro = 'todas' | 'pagadas' | 'saldo' | 'rendidas';

function situacion(o: OdPorRendir): { texto: string; tono: string } {
  if (o.ncPendientes > 0) return { texto: 'Falta NC', tono: 'tono-aviso' };
  if (o.estado === 'RENDIDA')
    return o.retornoPendiente
      ? { texto: 'Retorno en bodega', tono: 'tono-aviso' }
      : { texto: 'Rendida, por cerrar', tono: 'tono-info' };
  if (o.listaParaCerrar) return { texto: 'Pagada', tono: 'tono-ok' };
  if (o.entregasMarcadas) return { texto: 'Marcándose', tono: 'tono-info' };
  if (BigInt(o.porCobrar) > 0n) return { texto: 'Con saldo', tono: 'tono-aviso' };
  if (o.cobranza > 0) return { texto: 'Lleva cobranza', tono: 'tono-aviso' };
  return { texto: 'Por rendir', tono: 'tono-neutro' };
}

function Lista({ alAbrir }: { alAbrir: (odId: string) => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'rutas', 'por-rendir'],
    queryFn: () => api.get<OdPorRendir[]>('/tesoreria/rutas/por-rendir', { query: { empresaId } }),
  });
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [confirmar, setConfirmar] = useState(false);
  const [avance, setAvance] = useState<{
    hechas: number;
    total: number;
    filas: Array<{ numero: number; ok: boolean; texto: string }>;
  } | null>(null);
  const todas = q.data ?? [];
  const pagadas = todas.filter((o) => o.listaParaCerrar);
  const filas = todas.filter((o) =>
    filtro === 'pagadas'
      ? o.listaParaCerrar
      : filtro === 'saldo'
        ? BigInt(o.porCobrar) > 0n
        : filtro === 'rendidas'
          ? o.estado === 'RENDIDA'
          : true,
  );
  // Una por una, para ver el avance: cada ODD se rinde completa y se cierra (o informa por qué no).
  const cerrarPagadas = async () => {
    setConfirmar(false);
    const lote = [...pagadas];
    const salida: Array<{ numero: number; ok: boolean; texto: string }> = [];
    setAvance({ hechas: 0, total: lote.length, filas: [] });
    for (const o of lote) {
      try {
        const r = await api.post<Resultado>(
          `/tesoreria/rutas/${o.odId}/rendir-manual`,
          { empresaId, version: o.version, paradas: [], cobros: [] },
          { idempotencia: nuevaClaveIdempotencia('rendir-pagada') },
        );
        salida.push({
          numero: o.numero,
          ok: r.cerrada,
          texto: r.cerrada
            ? 'Cerrada'
            : r.retornoPendiente
              ? 'Rendida; falta el retorno'
              : 'Rendida',
        });
      } catch (e) {
        salida.push({ numero: o.numero, ok: false, texto: (e as Error).message });
      }
      setAvance({ hechas: salida.length, total: lote.length, filas: [...salida] });
    }
    await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
  };
  return (
    <>
      <div className="acciones-fila" role="tablist">
        {(
          [
            ['todas', `Todas (${todas.length})`],
            ['pagadas', `Pagadas (${pagadas.length})`],
            ['saldo', `Con saldo (${todas.filter((o) => BigInt(o.porCobrar) > 0n).length})`],
            [
              'rendidas',
              `Rendidas sin cerrar (${todas.filter((o) => o.estado === 'RENDIDA').length})`,
            ],
          ] as const
        ).map(([v, t]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={filtro === v}
            className={filtro === v ? 'primario' : ''}
            onClick={() => setFiltro(v)}
          >
            {t}
          </button>
        ))}
        <span className="espaciador" />
        <button
          type="button"
          disabled={!pagadas.length || !!(avance && avance.hechas < avance.total)}
          onClick={() => setConfirmar(true)}
        >
          Cerrar las pagadas ({pagadas.length})
        </button>
      </div>
      {q.isError && <PanelError error={q.error} />}
      {avance && (
        <section className="recuadro" aria-label="Cierre de las pagadas">
          <h3>
            Cierre de las pagadas · {avance.hechas} de {avance.total}
          </h3>
          <table className="grilla compacta">
            <tbody>
              {avance.filas.map((f) => (
                <tr key={f.numero}>
                  <td className="mono">ODD {f.numero}</td>
                  <td className={f.ok ? 'texto-ok' : 'texto-aviso'}>{f.texto}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {avance.hechas === avance.total && (
            <button type="button" onClick={() => setAvance(null)}>
              Listo
            </button>
          )}
        </section>
      )}
      <section className="recuadro">
        <h3>ODD por rendir</h3>
        <table className="grilla compacta" aria-label="ODD por rendir">
          <thead>
            <tr>
              <th>ODD</th>
              <th>Fecha</th>
              <th>Camión</th>
              <th>Chofer</th>
              <th>Recaudador</th>
              <th className="der">Paradas</th>
              <th className="der">Documentado</th>
              <th className="der">Pagado</th>
              <th className="der">Por cobrar</th>
              <th className="der">Crédito</th>
              <th>Situación</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filas.map((o) => {
              const s = situacion(o);
              return (
                <tr key={o.odId} data-od={o.odId}>
                  <td className="mono">
                    {o.numero}
                    {o.nombre ? ` · ${o.nombre}` : ''}
                  </td>
                  <td>{fecha(o.fechaNegocio)}</td>
                  <td>{o.patente ?? '—'}</td>
                  <td>{o.chofer ?? '—'}</td>
                  <td>{o.recaudador ?? '—'}</td>
                  <td className="der">{o.paradas}</td>
                  <td className="der mono">{pesos(o.documentado)}</td>
                  <td className="der mono">{pesos(o.pagado)}</td>
                  <td className={`der mono${BigInt(o.porCobrar) > 0n ? ' texto-aviso' : ''}`}>
                    {pesos(o.porCobrar)}
                  </td>
                  <td className="der mono">{pesos(o.credito)}</td>
                  <td>
                    <span className={`insignia ${s.tono}`}>{s.texto}</span>
                  </td>
                  <td>
                    <button type="button" className="primario" onClick={() => alAbrir(o.odId)}>
                      {o.estado === 'RENDIDA' ? 'Cerrar' : 'Rendir'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {q.isSuccess && !filas.length && (
              <tr>
                <td colSpan={12} className="vacio">
                  No hay ODD en esta lista.
                </td>
              </tr>
            )}
          </tbody>
          {filas.length > 1 && (
            <tfoot>
              <tr>
                <td colSpan={6}>Total ({filas.length} ODD)</td>
                <td className="der mono">{pesos(String(suma(filas.map((o) => o.documentado))))}</td>
                <td className="der mono">{pesos(String(suma(filas.map((o) => o.pagado))))}</td>
                <td className="der mono">{pesos(String(suma(filas.map((o) => o.porCobrar))))}</td>
                <td className="der mono">{pesos(String(suma(filas.map((o) => o.credito))))}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </section>
      {confirmar && (
        <Modal
          titulo="Cerrar las ODD pagadas"
          ancho="angosto"
          alCerrar={() => setConfirmar(false)}
          pie={
            <>
              <button type="button" onClick={() => setConfirmar(false)}>
                Volver
              </button>
              <button type="button" className="primario" onClick={() => void cerrarPagadas()}>
                Cerrar {pagadas.length} ODD
              </button>
            </>
          }
        >
          <p>
            Se rinden <b>entregadas completas</b>, sin cobros nuevos (lo de contado ya está pagado),
            y se cierran: ODD {pagadas.map((o) => o.numero).join(', ')}.
          </p>
          <p className="apagado">
            Si alguna volvió con devolución según el manifiesto, ábrela y ríndela sola.
          </p>
        </Modal>
      )}
    </>
  );
}

// ---------------------------------------------------------------- rendir una ODD

function Rendir({
  odId,
  alVolver,
  alHacerNc,
}: {
  odId: string;
  alVolver: () => void;
  alHacerNc?: ((documentoId: string) => void) | undefined;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'rutas', 'hoja', odId],
    queryFn: () => api.get<Hoja>(`/tesoreria/rutas/${odId}/hoja`, { query: { empresaId } }),
  });
  const [entrega, setEntrega] = useState<Record<string, Entrega>>({});
  const [entregado, setEntregado] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState<Record<string, string>>({});
  const [destino, setDestino] = useState<Record<string, Destino>>({});
  const [cobros, setCobros] = useState<CobroNuevo[]>([]);
  const [observacion, setObservacion] = useState('');
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const h = q.data;
  const paradas = h?.paradas ?? [];
  const deNota = (notaId: string) => paradas.find((p) => p.notaId === notaId);
  const nuevoCobro = (p: Parada, monto = ''): CobroNuevo => ({
    cobroId: crypto.randomUUID(),
    notaId: p.notaId,
    medio: medioPorCondicion(p.condicionPago),
    monto,
    banco: '',
    numero: '',
    fecha: '',
    referencia: '',
    verificada: true,
  });
  const poner = (id: string, cambio: Partial<CobroNuevo>) =>
    setCobros((xs) => xs.map((c) => (c.cobroId === id ? { ...c, ...cambio } : c)));
  const lineaInvalida = (p: Parada) =>
    entrega[p.notaId] === 'DEVOLUCION' &&
    p.lineas.some((l) => {
      const v = entregado[l.notaLineaId] ?? cargadoBase(l);
      return !cantidadValida(v) || Number(v) > Number(l.cargado ?? '0');
    });
  const cobroInvalido = (c: CobroNuevo) =>
    !c.monto ||
    BigInt(c.monto) <= 0n ||
    (c.medio === 'CHEQUE' && (!c.banco.trim() || !c.numero.trim() || !c.fecha));
  const conDevolucion = paradas.filter(
    (p) => !p.marcada && (entrega[p.notaId] ?? 'COMPLETA') !== 'COMPLETA',
  );
  // Volvió algo de una nota facturada: hay que decir si el cliente lo quiere otro día o no.
  const preguntaDestino = (p: Parada) =>
    p.documentos.length > 0 &&
    (entrega[p.notaId] === 'RECHAZADA' ||
      (entrega[p.notaId] === 'DEVOLUCION' &&
        p.lineas.some(
          (l) => Number(entregado[l.notaLineaId] ?? cargadoBase(l)) < Number(l.cargado ?? '0'),
        )));
  const problemas = [
    ...conDevolucion.filter(lineaInvalida).map((p) => `Revisa lo entregado de ${p.cliente}`),
    ...conDevolucion
      .filter((p) => preguntaDestino(p) && !destino[p.notaId])
      .map((p) => `Di si ${p.cliente} quiere lo devuelto otro día o no`),
    ...cobros.filter(cobroInvalido).map((c) => `Completa el cobro de ${deNota(c.notaId)?.cliente}`),
  ];
  const rendir = useMutation({
    mutationFn: () =>
      api.post<Resultado>(
        `/tesoreria/rutas/${odId}/rendir-manual`,
        {
          empresaId,
          version: h!.version,
          paradas: conDevolucion.map((p) => ({
            notaId: p.notaId,
            lineas: p.lineas.map((l) => ({
              notaLineaId: l.notaLineaId,
              entregado:
                entrega[p.notaId] === 'RECHAZADA'
                  ? '0'
                  : (entregado[l.notaLineaId] ?? cargadoBase(l)),
              motivoRechazo: motivo[p.notaId]?.trim() || null,
            })),
            observaciones: motivo[p.notaId]?.trim() || null,
            destinoRechazo: preguntaDestino(p) ? (destino[p.notaId] ?? null) : null,
          })),
          cobros: cobros.map((c) => ({
            cobroId: c.cobroId,
            notaId: c.notaId,
            medio: c.medio,
            monto: c.monto,
            referencia: c.medio === 'TRANSFERENCIA' ? c.referencia.trim() || null : null,
            banco: c.medio === 'CHEQUE' ? c.banco.trim() : null,
            numeroDocumento: c.medio === 'CHEQUE' ? c.numero.trim() : null,
            fechaDocumento: c.medio === 'CHEQUE' ? c.fecha : null,
            verificada: c.medio === 'TRANSFERENCIA' ? c.verificada : undefined,
          })),
          observacion: observacion.trim() || null,
        },
        { idempotencia: nuevaClaveIdempotencia('rendir-manual') },
      ),
    onSuccess: async (r) => {
      setResultado(r);
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
    },
    // Lo que alcanzó a quedar (paradas, cobros) no se repite: se recarga la hoja para reintentar.
    onError: () => void q.refetch(),
  });
  const reintentar = useMutation({
    mutationFn: async () => {
      const fresca = await q.refetch();
      return api.post<Resultado>(
        `/tesoreria/rutas/${odId}/rendir-manual`,
        { empresaId, version: fresca.data!.version, paradas: [], cobros: [] },
        { idempotencia: nuevaClaveIdempotencia('rendir-manual') },
      );
    },
    onSuccess: async (r) => {
      setResultado(r);
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
    },
  });
  const [imprimir, setImprimir] = useState<{
    reciboId: string;
    clienteId: string;
    nombre: string;
  } | null>(null);
  const terminar = useCallback(() => setImprimir(null), []);
  const impresion = imprimir && (
    <ImprimirRecibo
      reciboId={imprimir.reciboId}
      clienteId={imprimir.clienteId}
      nombre={imprimir.nombre}
      alTerminar={terminar}
    />
  );
  /** El cobro abonado: los de esta pantalla o los que ya venían en la hoja (un reintento, la app). */
  const cobroAbonado = (cobroId: string) => {
    const nuevo = cobros.find((c) => c.cobroId === cobroId);
    if (nuevo) {
      const p = deNota(nuevo.notaId);
      return p && { parada: p, medio: nuevo.medio, monto: nuevo.monto };
    }
    for (const p of paradas) {
      const c = p.cobros.find((x) => x.cobroId === cobroId);
      if (c) return { parada: p, medio: c.medio, monto: c.monto };
    }
    return undefined;
  };

  if (q.isError) return <PanelError error={q.error} />;
  if (!h) return <p className="apagado">Cargando…</p>;
  if (resultado)
    return (
      <section className="recuadro">
        <h3>
          ODD N° {resultado.numero} {resultado.cerrada ? 'rendida y cerrada' : 'rendida'}
        </h3>
        <ul>
          {resultado.entregas > 0 && (
            <li>{resultado.entregas} parada(s) con devolución registradas.</li>
          )}
          <li>{resultado.imputados.length} cobro(s) abonados a la cuenta de los clientes.</li>
          {resultado.porVerificar > 0 && (
            <li>
              {resultado.porVerificar} transferencia(s) quedan en Recepción de rutas ›
              Transferencias por verificar.
            </li>
          )}
          {resultado.retornoPendiente && (
            <li>
              Lo devuelto sigue en el camión: bodega lo recibe en Bodega › Retornos y después
              cierras la ODD aquí.
            </li>
          )}
          {resultado.ncPendientes > 0 && (
            <li>
              {resultado.ncPendientes === 1
                ? 'Un cliente no quiso lo devuelto: hazle la nota de crédito.'
                : `${resultado.ncPendientes} clientes no quisieron lo devuelto: hazles la nota de crédito.`}{' '}
              La ODD se cierra cuando estén hechas.
            </li>
          )}
        </ul>
        {resultado.imputados.length > 0 && (
          <table className="grilla compacta" aria-label="Comprobantes de los cobros">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Medio</th>
                <th className="der">Monto</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {resultado.imputados.map((i) => {
                const c = cobroAbonado(i.cobroId);
                return (
                  <tr key={i.reciboId}>
                    <td>{c?.parada.cliente ?? '—'}</td>
                    <td>{c ? NOMBRE_MEDIO[c.medio] : '—'}</td>
                    <td className="der mono">{c ? pesos(c.monto) : '—'}</td>
                    <td>
                      {c && (
                        <button
                          type="button"
                          onClick={() =>
                            setImprimir({
                              reciboId: i.reciboId,
                              clienteId: c.parada.clienteEntidadId,
                              nombre: c.parada.cliente,
                            })
                          }
                        >
                          Imprimir comprobante
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {resultado.errores.length > 0 && (
          <div className="aviso-error">
            <p>No se pudieron abonar {resultado.errores.length} cobro(s); la ODD sigue abierta:</p>
            <ul>
              {resultado.errores.map((e) => (
                <li key={e.cobroId}>{e.mensaje}</li>
              ))}
            </ul>
            {reintentar.isError && <PanelError error={reintentar.error} />}
            <button
              type="button"
              disabled={reintentar.isPending}
              onClick={() => reintentar.mutate()}
            >
              Reintentar
            </button>
          </div>
        )}
        <button type="button" className="primario" onClick={alVolver}>
          Listo
        </button>
        {impresion}
      </section>
    );

  const yaCobrado = paradas.flatMap((p) =>
    p.cobros
      .filter((c) => !c.anulado)
      .map((c) => ({ ...c, cliente: p.cliente, clienteEntidadId: p.clienteEntidadId })),
  );
  const total = (m: Medio) => suma(cobros.filter((c) => c.medio === m).map((c) => c.monto));
  const enRuta = h.estado === 'EN_RUTA';
  const quedaRetorno = h.retorno.pendiente || conDevolucion.length > 0;
  return (
    <>
      <div className="acciones-fila">
        <button type="button" onClick={alVolver}>
          ‹ Volver
        </button>
        <b>
          ODD N° {h.numero}
          {h.nombre ? ` · ${h.nombre}` : ''}
        </b>
        <span className="apagado">
          {fecha(h.fechaNegocio)} · {h.patente ?? 'sin patente'} · chofer {h.chofer ?? '—'} ·
          recaudador {h.recaudador ?? '—'}
        </span>
      </div>
      <section className="recuadro">
        <h3>1 · Paradas según el manifiesto</h3>
        <table className="grilla compacta" aria-label="Paradas de la ODD">
          <thead>
            <tr>
              <th>Parada</th>
              <th>Cliente</th>
              <th>Condición</th>
              <th>Documento</th>
              <th className="der">Total</th>
              <th className="der">Pagado</th>
              <th className="der">Saldo</th>
              <th>Entrega</th>
              <th />
            </tr>
          </thead>
          {paradas.map((p) => {
            const saldo = suma(p.documentos.map((d) => d.saldo));
            const debe = p.aCobrar.modo === 'CREDITO' ? 0n : BigInt(p.aCobrar.total);
            const anterior = p.aCobrar.modo === 'COBRANZA' ? debe - saldo : 0n;
            const e = entrega[p.notaId] ?? 'COMPLETA';
            return (
              <tbody key={p.notaId} className="parada">
                <tr>
                  <td className="mono">
                    {p.parada} · {p.numeroVisible}
                  </td>
                  <td>{p.cliente}</td>
                  <td>
                    {condicion(p.condicionPago)}
                    {p.cobroContraEntrega && <span className="texto-aviso"> · cobranza</span>}
                  </td>
                  <td className="mono">
                    {p.documentos.length
                      ? p.documentos
                          .map((d) => `${TIPO_DOC[d.tipoDte] ?? d.tipoDte} ${d.folio ?? '—'}`)
                          .join(', ')
                      : '—'}
                  </td>
                  <td className="der mono">
                    {pesos(String(suma(p.documentos.map((d) => d.total))))}
                  </td>
                  <td className="der mono">
                    {pesos(
                      String(
                        suma(p.documentos.map((d) => d.pagado)) +
                          suma(p.documentos.map((d) => d.notasCredito)),
                      ),
                    )}
                  </td>
                  <td className={`der mono${saldo > 0n && debe > 0n ? ' texto-aviso' : ''}`}>
                    {pesos(String(saldo))}
                    {anterior > 0n && (
                      <div className="apagado">+ deuda anterior {pesos(String(anterior))}</div>
                    )}
                  </td>
                  <td>
                    {p.marcada || !enRuta ? (
                      <span className="apagado">
                        {p.estado === 'ENTREGADA'
                          ? 'Entregada'
                          : p.estado === 'ENTREGA_PARCIAL'
                            ? 'Con devolución'
                            : p.estado === 'RECHAZADA'
                              ? 'Rechazada'
                              : 'Marcada'}
                        {p.ncPendiente && <span className="texto-aviso"> · falta NC</span>}
                      </span>
                    ) : (
                      <select
                        aria-label={`Entrega de ${p.cliente}`}
                        value={e}
                        onChange={(x) =>
                          setEntrega((m) => ({ ...m, [p.notaId]: x.target.value as Entrega }))
                        }
                      >
                        <option value="COMPLETA">Completa</option>
                        <option value="DEVOLUCION">Con devolución</option>
                        <option value="RECHAZADA">Rechazada</option>
                      </select>
                    )}
                  </td>
                  <td>
                    {p.ncPendiente?.documentoId && alHacerNc ? (
                      <button
                        type="button"
                        className="primario"
                        onClick={() => alHacerNc(p.ncPendiente!.documentoId!)}
                      >
                        Hacer NC
                      </button>
                    ) : (
                      debe > 0n &&
                      e !== 'RECHAZADA' && (
                        <button
                          type="button"
                          onClick={() => setCobros((xs) => [...xs, nuevoCobro(p, String(debe))])}
                        >
                          Cobrar {pesos(String(debe))}
                        </button>
                      )
                    )}
                  </td>
                </tr>
                {!p.marcada && enRuta && e !== 'COMPLETA' && (
                  <tr className="detalle-devolucion">
                    <td />
                    <td colSpan={8}>
                      {e === 'DEVOLUCION' && (
                        <table className="grilla compacta" aria-label={`Líneas de ${p.cliente}`}>
                          <thead>
                            <tr>
                              <th>SKU</th>
                              <th>Producto</th>
                              <th className="der">Cargado</th>
                              <th className="der">Entregado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.lineas.map((l) => {
                              const v = entregado[l.notaLineaId] ?? cargadoBase(l);
                              const malo =
                                !cantidadValida(v) || Number(v) > Number(l.cargado ?? '0');
                              return (
                                <tr key={l.notaLineaId}>
                                  <td className="mono">{l.sku}</td>
                                  <td>{l.descripcion}</td>
                                  <td className="der mono">{Number(l.cargado ?? '0')}</td>
                                  <td className="der">
                                    <input
                                      aria-label={`Entregado de ${l.sku}`}
                                      inputMode="decimal"
                                      className={`mono der cantidad${malo ? ' invalido' : ''}`}
                                      value={v}
                                      onChange={(x) =>
                                        setEntregado((m) => ({
                                          ...m,
                                          [l.notaLineaId]: x.target.value.replace(',', '.').trim(),
                                        }))
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                      {preguntaDestino(p) && (
                        <fieldset
                          className="destino-rechazo"
                          aria-label={`Lo devuelto de ${p.cliente}`}
                        >
                          <legend>Lo devuelto, ¿lo quiere otro día?</legend>
                          <label className="campo-linea">
                            <input
                              type="radio"
                              name={`destino-${p.notaId}`}
                              checked={destino[p.notaId] === 'NC'}
                              onChange={() => setDestino((m) => ({ ...m, [p.notaId]: 'NC' }))}
                            />{' '}
                            <b>No lo quiere</b>: va nota de crédito
                          </label>
                          <label className="campo-linea">
                            <input
                              type="radio"
                              name={`destino-${p.notaId}`}
                              checked={destino[p.notaId] === 'REENTREGA'}
                              onChange={() =>
                                setDestino((m) => ({ ...m, [p.notaId]: 'REENTREGA' }))
                              }
                            />{' '}
                            <b>Lo quiere otro día</b>: vuelve a salir con la misma factura
                          </label>
                        </fieldset>
                      )}
                      <label className="campo-linea">
                        Motivo (opcional){' '}
                        <input
                          aria-label={`Motivo de ${p.cliente}`}
                          maxLength={200}
                          value={motivo[p.notaId] ?? ''}
                          onChange={(x) => setMotivo((m) => ({ ...m, [p.notaId]: x.target.value }))}
                        />
                      </label>
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </section>
      <section className="recuadro">
        <h3>2 · Lo cobrado en la ruta</h3>
        {yaCobrado.length > 0 && (
          <table className="grilla compacta" aria-label="Cobros ya anotados">
            <thead>
              <tr>
                <th>Ya anotado</th>
                <th>Medio</th>
                <th className="der">Monto</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {yaCobrado.map((c) => (
                <tr key={c.cobroId}>
                  <td>
                    {c.cliente}{' '}
                    <span className="apagado">({c.origen === 'APP' ? 'app' : 'a mano'})</span>
                  </td>
                  <td>{NOMBRE_MEDIO[c.medio]}</td>
                  <td className="der mono">{pesos(c.monto)}</td>
                  <td>
                    {c.reciboId ? (
                      <>
                        Abonado{' '}
                        <button
                          type="button"
                          onClick={() =>
                            setImprimir({
                              reciboId: c.reciboId!,
                              clienteId: c.clienteEntidadId,
                              nombre: c.cliente,
                            })
                          }
                        >
                          Imprimir comprobante
                        </button>
                      </>
                    ) : (
                      (c.estadoTesoreria ?? 'Por recibir')
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <table className="grilla compacta" aria-label="Cobros de la ruta">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Medio</th>
              <th className="der">Monto</th>
              <th>Detalle</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {cobros.map((c) => (
              <tr key={c.cobroId}>
                <td>
                  <select
                    aria-label="Cliente del cobro"
                    value={c.notaId}
                    onChange={(x) => poner(c.cobroId, { notaId: x.target.value })}
                  >
                    {paradas.map((p) => (
                      <option key={p.notaId} value={p.notaId}>
                        {p.parada} · {p.cliente}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    aria-label="Medio de pago"
                    value={c.medio}
                    onChange={(x) => poner(c.cobroId, { medio: x.target.value as Medio })}
                  >
                    {(Object.keys(NOMBRE_MEDIO) as Medio[]).map((m) => (
                      <option key={m} value={m}>
                        {NOMBRE_MEDIO[m]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="der">
                  <input
                    aria-label="Monto"
                    inputMode="numeric"
                    className="mono der monto"
                    value={c.monto}
                    onChange={(x) => poner(c.cobroId, { monto: soloDigitos(x.target.value) })}
                  />
                </td>
                <td>
                  {c.medio === 'CHEQUE' ? (
                    <span className="detalle-medio">
                      <input
                        aria-label="Banco"
                        placeholder="Banco"
                        value={c.banco}
                        onChange={(x) => poner(c.cobroId, { banco: x.target.value })}
                      />
                      <input
                        aria-label="N° de cheque"
                        placeholder="N° cheque"
                        value={c.numero}
                        onChange={(x) => poner(c.cobroId, { numero: x.target.value })}
                      />
                      <input
                        type="date"
                        aria-label="Fecha del cheque"
                        value={c.fecha}
                        onChange={(x) => poner(c.cobroId, { fecha: x.target.value })}
                      />
                    </span>
                  ) : c.medio === 'TRANSFERENCIA' ? (
                    <span className="detalle-medio">
                      <input
                        aria-label="N° de operación"
                        placeholder="N° operación (opcional)"
                        value={c.referencia}
                        onChange={(x) => poner(c.cobroId, { referencia: x.target.value })}
                      />
                      <label className="campo-linea">
                        <input
                          type="checkbox"
                          checked={c.verificada}
                          onChange={(x) => poner(c.cobroId, { verificada: x.target.checked })}
                        />{' '}
                        La vi en el banco
                      </label>
                    </span>
                  ) : (
                    <span className="apagado">—</span>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    aria-label="Quitar cobro"
                    onClick={() => setCobros((xs) => xs.filter((x) => x.cobroId !== c.cobroId))}
                  >
                    Quitar
                  </button>
                </td>
              </tr>
            ))}
            {!cobros.length && (
              <tr>
                <td colSpan={5} className="vacio">
                  Sin cobros nuevos. Usa «Cobrar» en la parada o agrega uno.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="acciones-fila">
          <button
            type="button"
            disabled={!paradas.length}
            onClick={() => paradas[0] && setCobros((xs) => [...xs, nuevoCobro(paradas[0]!)])}
          >
            + Agregar cobro
          </button>
          <span className="espaciador" />
          <span>
            Efectivo <b className="mono">{pesos(String(total('EFECTIVO')))}</b> · Cheques{' '}
            <b className="mono">{pesos(String(total('CHEQUE')))}</b> · Transferencias{' '}
            <b className="mono">{pesos(String(total('TRANSFERENCIA')))}</b>
          </span>
        </div>
      </section>
      <section className="recuadro">
        <h3>3 · Confirmar</h3>
        <label className="campo-linea observacion">
          Observación (opcional){' '}
          <input
            aria-label="Observación"
            maxLength={500}
            value={observacion}
            onChange={(x) => setObservacion(x.target.value)}
          />
        </label>
        <p className="apagado">
          {enRuta ? 'Las paradas sin marcar se rinden entregadas completas. ' : ''}
          {cobros.length
            ? `${cobros.length} cobro(s) se abonan a la cuenta de cada cliente (el efectivo queda en tu caja). `
            : 'Sin cobros nuevos. '}
          {quedaRetorno
            ? 'Lo devuelto queda en el camión hasta que bodega lo reciba; después cierras la ODD aquí.'
            : 'La ODD queda cerrada.'}
        </p>
        {problemas.length > 0 && <p className="texto-aviso">{problemas[0]}</p>}
        {rendir.isError && <PanelError error={rendir.error} alCerrar={() => rendir.reset()} />}
        <div className="acciones-fila">
          <span className="espaciador" />
          <button
            type="button"
            className="primario"
            disabled={rendir.isPending || problemas.length > 0}
            onClick={() => rendir.mutate()}
          >
            {rendir.isPending
              ? 'Rindiendo…'
              : !enRuta
                ? 'Cerrar ODD'
                : quedaRetorno
                  ? 'Rendir ODD'
                  : 'Rendir y cerrar ODD'}
          </button>
        </div>
      </section>
      {impresion}
    </>
  );
}
